import { useState, useRef, useCallback } from 'react';
import api from '../../services/api';

const RESULTADO_OPTS = [
  { value: 'aprovado',              label: 'Aprovado' },
  { value: 'reprovado',             label: 'Reprovado' },
  { value: 'aprovado_com_restricao',label: 'Ap. c/ Restrição' },
];

function StatusBadge({ status }) {
  const cfg = {
    pendente:   { cls: 'bg-slate-800 text-slate-400 border-slate-700',          label: 'Pendente' },
    extraindo:  { cls: 'bg-amber-900/40 text-amber-300 border-amber-800/40',    label: 'Extraindo...' },
    ok:         { cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-800/40', label: 'Extraído' },
    sem_tag:    { cls: 'bg-amber-900/40 text-amber-300 border-amber-800/40',    label: 'TAG não encontrada' },
    erro:       { cls: 'bg-red-900/40 text-red-300 border-red-800/40',          label: 'Erro' },
    importado:  { cls: 'bg-blue-900/40 text-blue-300 border-blue-800/40',       label: 'Importado' },
  }[status] || { cls: '', label: status };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${cfg.cls}`}>{cfg.label}</span>
  );
}

function CertificadoCard({ item, idx, onUpdate, onRemove, onImport, importando }) {
  const [expanded, setExpanded] = useState(true);
  const d = item.data;

  const field = (label, key, type = 'text') => (
    <div>
      <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">{label}</p>
      {type === 'select' ? (
        <select
          value={d[key] || ''}
          onChange={e => onUpdate(idx, key, e.target.value)}
          className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-xs text-slate-200 focus:outline-none focus:border-blue-500"
        >
          <option value="">— selecione —</option>
          {RESULTADO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input
          type={type}
          value={d[key] || ''}
          onChange={e => onUpdate(idx, key, e.target.value)}
          className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-xs text-slate-200 focus:outline-none focus:border-blue-500"
        />
      )}
    </div>
  );

  return (
    <div className={`border rounded-xl overflow-hidden ${item.status === 'importado' ? 'border-blue-800/40 opacity-60' : 'border-[var(--surface-4)]'}`}>
      {/* Card header */}
      <div
        className="flex items-center justify-between px-4 py-2.5 bg-[var(--surface-3)] cursor-pointer"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-slate-500 text-xs flex-shrink-0">📄</span>
          <span className="text-xs text-slate-300 font-medium truncate">{item.fileName}</span>
          {d.tag && (
            <span className="font-mono text-[10px] bg-[var(--surface-4)] text-blue-300 px-1.5 py-0.5 rounded flex-shrink-0">{d.tag}</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusBadge status={item.status} />
          <span className="text-slate-600 text-xs">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Card body */}
      {expanded && item.status !== 'pendente' && item.status !== 'extraindo' && (
        <div className="p-4 space-y-3 bg-[var(--surface-2)]">
          {item.status === 'erro' ? (
            <p className="text-xs text-red-400">{item.erro}</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                {field('TAG do Instrumento', 'tag')}
                {field('Resultado', 'result', 'select')}
                {field('Data da Calibração', 'calibrationDate', 'date')}
                {field('Próxima Calibração', 'nextCalibrationDate', 'date')}
                {field('Laboratório / Fornecedor', 'fornecedor')}
                {field('Nº Certificado', 'numeroCertificado')}
                {field('Acreditação', 'acreditacaoFornecedor')}
              </div>
              {d.observacoes !== undefined && field('Observações', 'observacoes')}

              {item.status !== 'importado' && (
                <div className="flex items-center justify-between pt-2 border-t border-[var(--surface-4)]">
                  {!d.tag && (
                    <p className="text-[10px] text-amber-400">⚠ TAG não identificada — preencha manualmente</p>
                  )}
                  <div className="flex gap-2 ml-auto">
                    <button onClick={() => onRemove(idx)} className="text-xs px-3 py-1.5 text-slate-500 hover:text-red-400 transition-colors">
                      Remover
                    </button>
                    <button
                      onClick={() => onImport(idx)}
                      disabled={importando || !d.tag || !d.result || !d.calibrationDate || !d.nextCalibrationDate}
                      className="text-xs px-4 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors disabled:opacity-40"
                    >
                      {importando ? 'Importando...' : '✓ Criar calibração'}
                    </button>
                  </div>
                </div>
              )}
              {item.status === 'importado' && (
                <p className="text-xs text-blue-400 pt-1">✓ Calibração criada com sucesso</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function CertificateImportModal({ isOpen, onClose, onImportComplete }) {
  const [items, setItems] = useState([]); // { fileName, file, status, data, erro }
  const [dragging, setDragging] = useState(false);
  const [importandoIdx, setImportandoIdx] = useState(null);
  const fileRef = useRef(null);

  const reset = () => { setItems([]); };
  const handleClose = () => { reset(); onClose(); };

  const extractFromFile = async (file, idx) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, status: 'extraindo' } : it));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/ai/extract-certificate', fd, {
        headers: { 'Content-Type': 'multipart/form-data', 'x-filename': file.name },
      });
      const ext = res.data.extracted || {};
      const hasTag = !!ext.tag;
      setItems(prev => prev.map((it, i) => i === idx
        ? { ...it, status: hasTag ? 'ok' : 'sem_tag', data: { ...ext } }
        : it
      ));
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao extrair dados do certificado';
      setItems(prev => prev.map((it, i) => i === idx ? { ...it, status: 'erro', erro: msg } : it));
    }
  };

  const addFiles = useCallback(async (files) => {
    const valid = Array.from(files).filter(f => f.type === 'application/pdf' || f.name.match(/\.pdf$/i));
    if (!valid.length) return;

    const startIdx = items.length;
    const newItems = valid.map(f => ({
      fileName: f.name,
      file: f,
      status: 'pendente',
      data: { tag: '', result: '', calibrationDate: '', nextCalibrationDate: '', fornecedor: '', acreditacaoFornecedor: '', numeroCertificado: '', observacoes: '' },
      erro: null,
    }));
    setItems(prev => [...prev, ...newItems]);

    for (let i = 0; i < newItems.length; i++) {
      await extractFromFile(valid[i], startIdx + i);
    }
  }, [items.length]);

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const updateField = (idx, key, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, data: { ...it.data, [key]: value } } : it));
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const importItem = async (idx) => {
    const item = items[idx];
    if (!item) return;
    setImportandoIdx(idx);
    try {
      // Busca o instrumento pela TAG para obter o ID
      const searchRes = await api.get(`/instruments?tag=${encodeURIComponent(item.data.tag)}&limit=1`);
      const instruments = searchRes.data.dados || searchRes.data || [];
      const instrumento = instruments.find(i => i.tag === item.data.tag);
      if (!instrumento) {
        setItems(prev => prev.map((it, i) => i === idx ? { ...it, status: 'erro', erro: `TAG "${item.data.tag}" não encontrada no sistema` } : it));
        return;
      }

      // Cria a calibração
      const body = new FormData();
      body.append('instrumentId', instrumento.id || instrumento._id);
      body.append('calibrationType', 'externa');
      body.append('data', item.data.calibrationDate);
      body.append('proximaData', item.data.nextCalibrationDate);
      body.append('resultado', item.data.result);
      if (item.data.fornecedor) body.append('fornecedor', item.data.fornecedor);
      if (item.data.acreditacaoFornecedor) body.append('acreditacaoFornecedor', item.data.acreditacaoFornecedor);
      if (item.data.numeroCertificado) body.append('numeroCertificado', item.data.numeroCertificado);
      if (item.data.observacoes) body.append('observacoes', item.data.observacoes);
      body.append('statusLaudo', 'em_revisao');
      // Anexa o PDF como certificado
      body.append('arquivoCertificado', item.file, item.fileName);

      await api.post('/calibrations', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      setItems(prev => prev.map((it, i) => i === idx ? { ...it, status: 'importado' } : it));
      if (onImportComplete) onImportComplete();
    } catch (err) {
      const msg = err.response?.data?.mensagem || err.response?.data?.error || 'Erro ao criar calibração';
      setItems(prev => prev.map((it, i) => i === idx ? { ...it, status: 'erro', erro: msg } : it));
    } finally {
      setImportandoIdx(null);
    }
  };

  if (!isOpen) return null;

  const importados = items.filter(i => i.status === 'importado').length;
  const pendentes  = items.filter(i => i.status !== 'importado').length;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--surface-4)] flex-shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-white">Importar Certificados via IA</h2>
            <p className="text-xs text-slate-500 mt-0.5">Upload de PDFs — Gemini extrai os dados automaticamente</p>
          </div>
          <div className="flex items-center gap-3">
            {items.length > 0 && (
              <span className="text-xs text-slate-500">
                {importados}/{items.length} importados
              </span>
            )}
            <button onClick={handleClose} className="text-slate-500 hover:text-slate-300 text-lg leading-none">✕</button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${dragging ? 'border-blue-500 bg-blue-500/10' : 'border-[var(--surface-4)] hover:border-slate-500 hover:bg-[var(--surface-3)]'}`}
          >
            <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden" onChange={e => addFiles(e.target.files)} />
            <div className="text-3xl mb-2">📋</div>
            <p className="text-sm text-slate-300">Arraste os PDFs ou clique para selecionar</p>
            <p className="text-xs text-slate-600 mt-1">Múltiplos arquivos — apenas .pdf</p>
          </div>

          {/* Fila de certificados */}
          {items.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{items.length} certificado(s) na fila</p>
                {pendentes > 0 && (
                  <button
                    onClick={() => setItems([])}
                    className="text-[10px] text-slate-600 hover:text-red-400 transition-colors"
                  >
                    Limpar tudo
                  </button>
                )}
              </div>
              {items.map((item, idx) => (
                <CertificadoCard
                  key={idx}
                  item={item}
                  idx={idx}
                  onUpdate={updateField}
                  onRemove={removeItem}
                  onImport={importItem}
                  importando={importandoIdx === idx}
                />
              ))}
            </div>
          )}

          {items.length === 0 && (
            <div className="p-3 bg-[var(--surface-3)] rounded-lg border border-[var(--surface-4)]">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Como funciona</p>
              <ul className="space-y-1 text-xs text-slate-400">
                <li>1. Faça upload de um ou mais PDFs de certificados</li>
                <li>2. A IA extrai TAG, datas, resultado, laboratório e número do certificado</li>
                <li>3. Revise e ajuste os dados extraídos se necessário</li>
                <li>4. Clique em "Criar calibração" para cada certificado</li>
                <li>5. A calibração é criada em status "Em revisão" para assinatura</li>
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[var(--surface-4)] flex-shrink-0">
          <p className="text-xs text-slate-600">
            {importados > 0 ? `${importados} calibração(ões) criada(s) com sucesso` : 'Cada certificado gera uma calibração externa em revisão'}
          </p>
          <button onClick={handleClose} className="text-xs px-4 py-2 bg-[var(--surface-4)] hover:bg-slate-600 text-slate-200 rounded-lg transition-colors">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
